import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isSeoManagedPath } from "@/seo/paths";

const upsert = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

/**
 * Keeps <head> in sync with the SEO route manifest during client-side navigation.
 * Prerendered HTML already contains the same tags; this only matters after in-app navigation.
 */
const SeoHead = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    if (!isSeoManagedPath(pathname)) {
      document.getElementById("seo-graph")?.remove();
      return;
    }
    // Content data is loaded on demand so it stays out of the main bundle.
    import("@/seo/routes").then(({ DEFAULT_OG_IMAGE, getSeoForPath, jsonLdFor }) => {
    if (cancelled) return;
    const r = getSeoForPath(pathname);
    const existing = document.getElementById("seo-graph");
    if (!r) {
      existing?.remove();
      return;
    }
    // Trailing-slash URLs: show the canonical address without the slash.
    if (pathname !== "/" && pathname.endsWith("/")) {
      window.history.replaceState(window.history.state, "", pathname.replace(/\/+$/, "") + window.location.search + window.location.hash);
    }
    document.title = r.title;
    upsert('meta[name="description"]', "name", "description", r.description);
    upsert('meta[name="robots"]', "name", "robots", r.noindex ? "noindex, nofollow" : "index, follow");
    upsert('meta[property="og:type"]', "property", "og:type", r.ogType);
    upsert('meta[property="og:title"]', "property", "og:title", r.title);
    upsert('meta[property="og:description"]', "property", "og:description", r.description);
    upsert('meta[property="og:url"]', "property", "og:url", r.canonical);
    upsert('meta[property="og:image"]', "property", "og:image", DEFAULT_OG_IMAGE);
    upsert('meta[name="twitter:title"]', "name", "twitter:title", r.title);
    upsert('meta[name="twitter:description"]', "name", "twitter:description", r.description);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = r.canonical;
    link.dataset.canonicalSource = "page";
    link.dataset.canonicalPath = pathname;

    let script = existing as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "seo-graph";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLdFor(r));
    });
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return null;
};

export default SeoHead;
